﻿import React, { useState } from "react";
import { motion as Motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import headcoach from "../assets/headcoach.webp";
import asscoach from "../assets/ass.coach.webp";

const About = () => {
  const assistantCoaches = [1];
  const [showFullAssistantBio, setShowFullAssistantBio] = useState(false);

  const assistantBioPreview =
    "Gideon is a Ghanaian-born coach and athlete who balances a decorated playing career with a passion for player development.";
  const assistantBioMore =
    "A former standout at USAO with three SAC Finals appearances and multiple All-Conference selections, he currently captains Oklahoma United. As an Assistant Coach, Gideon leverages his current experience as a team leader on the pitch to mentor our players in leadership, discipline, and elite-level technical execution.";

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    <div className="flex flex-col pt-16 md:pt-20 bg-[#f5f5f5]">
      <Helmet>
        <title>Reflection Futbol Academy | Meet Our Coaches</title>
        <meta
          name="description"
          content="Learn about RFA's head coach Robert Owusu and assistant coach Gideon Bossman. Expert guidance for youth technical and tactical football development."
        />
        <link
          rel="canonical"
          href="https://reflectionfutbolacademy.com/about"
        />

        {/* Social SEO (Open Graph) */}
        <meta
          property="og:title"
          content="Expert Football Coaching | Reflection Futbol Academy"
        />
        <meta
          property="og:description"
          content="Meet the professional coaches behind RFA's success. Technical and tactical growth for your child."
        />
      </Helmet>
      {/* 1. Hero Section */}
      <section className="relative py-12 md:py-20 bg-[#7D192A] flex items-center justify-center text-center overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.unsplash.com/photo-1574629810360-7efbbe195018?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=60"
            srcSet="https://images.unsplash.com/photo-1574629810360-7efbbe195018?ixlib=rb-4.0.3&auto=format&fit=crop&w=640&q=60 640w,
                    https://images.unsplash.com/photo-1574629810360-7efbbe195018?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=60 1200w"
            sizes="100vw"
            alt="Football training session representing RFA academy spirit"
            fetchPriority="high"
            width={1200}
            height={800}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 w-full max-w-7xl mx-auto site-gutter">
          <h1 className="page-hero-title text-white uppercase tracking-tight">
            About Our Coaches
          </h1>
          <div className="mt-3 md:mt-4 h-1 w-16 md:h-2 md:w-32 bg-[#F4B400] mx-auto rounded-full" />
        </div>
      </section>

      {/* 2. Main Content Container */}
      <section className="py-20 bg-[#f5f5f5]">
        <div className="max-w-7xl mx-auto site-gutter">
          {/* Coaches Profiles */}
          <Motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            className="mb-24"
          >
            {/* Head Coach Card */}
            <Motion.div
              variants={itemVariants}
              className="surface-card rounded-3xl shadow-soft overflow-hidden border-t-8 border-[#7D192A] p-8 md:p-12 mb-12 flex flex-col md:flex-row gap-10 items-center"
            >
              <div className="w-full md:w-1/3 aspect-[4/5] bg-gray-200 rounded-2xl flex-shrink-0 overflow-hidden shadow-2xl">
                <img
                  src={headcoach}
                  alt="Head Coach Robert Owusu"
                  width={600}
                  height={750}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="flex-grow space-y-4 text-left">
                <span className="text-[#7D192A] font-inter font-bold text-sm uppercase tracking-widest">
                  Head Coach
                </span>
                <h2 className="text-3xl md:text-4xl font-inter font-extrabold text-[#111111] uppercase">
                  Robert Owusu
                </h2>
                <p className="body-text text-gray-600">
                  Robert Owusu brings over a decade of playing and coaching
                  experience at semi-professional and grassroots levels. With
                  UEFA C Licence credentials and a deep passion for youth
                  development, Coach Robert has built the academy from the
                  ground up, producing technically skilled and tactically aware
                  players who compete at regional and national levels.
                </p>
                <p className="body-text text-gray-600">
                  His coaching philosophy centers on building the complete
                  footballer: technical excellence, tactical intelligence,
                  physical conditioning, and mental fortitude.
                </p>
              </div>
            </Motion.div>

            {/* Assistant Coaches Grid */}
            <div className="grid grid-cols-1 gap-8 max-w-xl">
              {assistantCoaches.map((_, index) => (
                <Motion.div
                  key={index}
                  variants={itemVariants}
                  className="surface-card rounded-3xl shadow-soft p-10 text-center flex flex-col items-center space-y-4"
                >
                  <div className="w-40 h-40 rounded-full bg-gray-200 mb-2 overflow-hidden shadow-2xl">
                    <img
                      src={asscoach}
                      alt="Assistant coach Gideon Bossman"
                      width={160}
                      height={160}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div>
                    <span className="text-[#7D192A] font-inter font-bold text-xs uppercase tracking-widest">
                      Assistant Coach
                    </span>
                    <h3 className="text-2xl font-inter font-extrabold text-[#111111] uppercase">
                      Gideon Bossman
                    </h3>
                  </div>
                  <p className="body-text text-gray-600 max-w-xs">
                    {assistantBioPreview}
                    {showFullAssistantBio ? ` ${assistantBioMore}` : ""}
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowFullAssistantBio((prev) => !prev)}
                    className="text-[#7D192A] font-inter font-bold text-xs uppercase tracking-widest hover:text-[#F4B400] transition-colors"
                  >
                    {showFullAssistantBio ? "Show Less" : "More"}
                  </button>
                </Motion.div>
              ))}
            </div>
          </Motion.div>

          {/* Section 2: Our Aim & Goals */}
          <Motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-24 items-start pt-20 border-t border-gray-200"
          >
            <Motion.div variants={itemVariants} className="space-y-6 text-left">
              <div className="space-y-2">
                <h2 className="text-3xl md:text-4xl font-inter font-extrabold text-[#111111] uppercase tracking-tight">
                  Our Aim
                </h2>
                <div className="h-1 w-full bg-[#7D192A]" />
              </div>
              <div className="space-y-4 body-text text-gray-600">
                <p>
                  Our aim is to provide a world-class football development
                  environment that nurtures young talent from grassroots to
                  elite level. We believe every player deserves
                  professional-grade coaching, structured development pathways,
                  and the opportunity to reach their ceiling.
                </p>
                <p>
                  We strive to be the most respected football academy in the
                  region, known not just for producing great footballers, but
                  great people.
                </p>
              </div>
            </Motion.div>

            <Motion.div variants={itemVariants} className="space-y-6 text-left">
              <div className="space-y-2">
                <h2 className="text-3xl md:text-4xl font-inter font-extrabold text-[#111111] uppercase tracking-tight">
                  Our Goals
                </h2>
                <div className="h-1 w-full bg-[#7D192A]" />
              </div>
              <ul className="space-y-4 font-inter text-gray-600">
                {[
                  "Develop technically proficient and tactically intelligent players",
                  "Provide professional-level training accessible to all youth",
                  "Build mental resilience, discipline and leadership in every player",
                  "Create clear pathways from youth to senior competitive football",
                  "Foster a community built on respect, teamwork and excellence",
                  "Produce players who represent their communities with pride",
                ].map((goal, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="shrink-0 text-[#7D192A] font-black text-xl leading-none mt-0.5">
                      •
                    </span>
                    <span className="leading-relaxed">{goal}</span>
                  </li>
                ))}
              </ul>
            </Motion.div>
          </Motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
